import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Link2, Copy, Trash2, Eye, Edit3, Clock, Users, Plus, Loader2, ExternalLink, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FormCollaboration = ({ formId, isOpen, onClose }) => {
  const { token } = useAuth();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [accessType, setAccessType] = useState('view');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const fetchCollaborators = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/forms/${formId}/collaboration`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCollaborators(data.collaborators || []);
      }
    } catch (error) {
      toast.error('Failed to load collaboration links');
    } finally {
      setLoading(false);
    }
  }, [formId, token]);

  useEffect(() => {
    if (isOpen && formId) {
      fetchCollaborators();
    }
  }, [isOpen, formId, fetchCollaborators]);

  const handleCreateLink = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/forms/${formId}/collaboration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          access_type: accessType,
          expires_in_days: expiresInDays ? parseInt(expiresInDays) : null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Collaboration link created');
        setAccessType('view');
        setExpiresInDays('');
        fetchCollaborators();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create link');
      }
    } catch (error) {
      toast.error('Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeLink = async (linkId) => {
    try {
      const response = await fetch(`${API_URL}/api/forms/collaboration/${linkId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Link revoked');
        fetchCollaborators();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to revoke link');
      }
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  const copyToClipboard = (collab) => {
    const url = `${window.location.origin}/forms/collaborate/${collab.link_token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(collab.id);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Form Collaboration
          </DialogTitle>
          <DialogDescription>
            Generate shareable links to collaborate on this form
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Create New Link */}
          <Card>
            <CardContent className="pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Create New Link</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Access Type</Label>
                  <Select value={accessType} onValueChange={setAccessType}>
                    <SelectTrigger data-testid="collab-access-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          View Only
                        </div>
                      </SelectItem>
                      <SelectItem value="edit">
                        <div className="flex items-center gap-2">
                          <Edit3 className="w-4 h-4" />
                          Can Edit
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Expires In</Label>
                  <Select value={expiresInDays || 'never'} onValueChange={(val) => setExpiresInDays(val === 'never' ? '' : val)}>
                    <SelectTrigger data-testid="collab-expiry">
                      <SelectValue placeholder="Never" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never</SelectItem>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button 
                onClick={handleCreateLink} 
                disabled={creating} 
                className="mt-4"
                data-testid="create-collab-link-btn"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Generate Link
              </Button>
            </CardContent>
          </Card>

          {/* Existing Links */}
          <div>
            <h3 className="font-medium text-slate-900 mb-3">
              Active Links ({collaborators.filter(c => c.is_active).length})
            </h3>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : collaborators.filter(c => c.is_active).length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Link2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No active collaboration links</p>
              </div>
            ) : (
              <div className="space-y-3">
                {collaborators.filter(c => c.is_active).map((collab) => {
                  const expired = isExpired(collab.expires_at);
                  
                  return (
                    <div 
                      key={collab.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        expired ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                      }`}
                      data-testid={`collab-link-${collab.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          collab.access_type === 'edit' 
                            ? 'bg-blue-100 text-blue-600' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {collab.access_type === 'edit' ? (
                            <Edit3 className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant={collab.access_type === 'edit' ? 'default' : 'secondary'}>
                              {collab.access_type === 'edit' ? 'Edit Access' : 'View Only'}
                            </Badge>
                            {expired && (
                              <Badge variant="destructive">Expired</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1 flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Expires: {formatDate(collab.expires_at)}
                            </span>
                            <span>•</span>
                            <span>{collab.access_count} views</span>
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(collab)}
                          disabled={expired}
                        >
                          {copiedId === collab.id ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/forms/collaborate/${collab.link_token}`, '_blank')}
                          disabled={expired}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRevokeLink(collab.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FormCollaboration;
